import { useState } from "react";

export type AdminFormStatus = "idle" | "pending" | "success" | "error";

/**
 * The save-flow scaffolding every admin form (`features/admin/*.tsx`)
 * otherwise hand-rolls identically: a status enum, an error string, and a
 * submit handler that transitions between them around one async call.
 * `onSaved` mirrors each form's existing callback (`onSaved`/`onCreated`/
 * `onCleared`) — called with the fresh result exactly as before, so a save
 * can still push straight into state per `CLAUDE.md`'s `useResource` note
 * rather than triggering a refetch.
 */
export function useAdminForm<T>(
  save: () => Promise<T>,
  onSaved: (result: T) => void,
): { status: AdminFormStatus; error: string | null; submit: () => Promise<void> } {
  const [status, setStatus] = useState<AdminFormStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setStatus("pending");
    setError(null);
    try {
      const result = await save();
      setStatus("success");
      onSaved(result);
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return { status, error, submit };
}
